import { IconButton } from '@lastbrand/web-ui/icon-button'
import { BagPlusIcon } from '@lastbrand/web-ui/icons'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo } from 'react'

import { getSelectedOptionsForProductCard } from '@src/components/product-card/product-card.helpers'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import {
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { RECOMMENDATION_TRACKING_KEYS } from '@src/quince-site-common/src/organisms/appLayout/molecules/cartPopUp/molecules/cartPopupTwo/molecules/recommendedForYou/molecules/product-list-carousel/product-list-carousel.constants'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { handleTrackAddedToCartAlgolia } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import type { AddToCartButtonProps } from './add-to-cart-button.types'

/**
 * Merge onto the product card root so `addToBagReveal="hover"` reveals the bag
 * button when the user hovers anywhere on the card, or moves keyboard focus
 * into any of its descendants.
 */
export const addToCartButtonHoverRevealParentCss = css.raw({
  '@media (hover: hover)': {
    _hover: {
      '& [data-slot="product-tile-add-bag"]': {
        opacity: 1,
        // eslint-disable-next-line i18next/no-literal-string -- CSS value, not user-facing copy
        pointerEvents: 'auto',
      },
    },
    '&:has(:focus-visible)': {
      '& [data-slot="product-tile-add-bag"]': {
        opacity: 1,
        // eslint-disable-next-line i18next/no-literal-string -- CSS value, not user-facing copy
        pointerEvents: 'auto',
      },
    },
  },
})

export function AddToCartButton({
  productCard,
  selectedVariant,
  productClickTrackConfig,
  searchQuery,
  position,
  tabData,
  quickBuyOverride,
  disabled,
  visible = true,
  addToBagReveal = 'hover',
  addToBagAriaLabel,
  tileVariant = 'default',
  addToBagButtonCss,
  productSearchObjectId: productSearchObjectIdProp,
  showBuyNowOnMobile = false,
}: AddToCartButtonProps) {
  const { t } = useTranslation('common')
  const resolvedAddToBagAriaLabel = addToBagAriaLabel ?? t('add_to_bag')
  const isMini = tileVariant === 'mini'
  const isCompact = tileVariant === 'compact'

  const productSearchObjectIdComputed = useMemo(() => {
    if (!selectedVariant || !productCard.product.relatedIds.spid) return ''
    return `${productCard.product.relatedIds.spid}_${selectedVariant.displayConfig.value}`
  }, [selectedVariant, productCard.product.relatedIds.spid])
  const productSearchObjectId = productSearchObjectIdProp ?? productSearchObjectIdComputed

  const handleClick = useCallback(() => {
    const prod = productCard.product
    const productProperties = [
      RECOMMENDATION_TRACKING_KEYS.REC_MODULE_ID,
      RECOMMENDATION_TRACKING_KEYS.IS_CART_MODAL,
      RECOMMENDATION_TRACKING_KEYS.REC_ELEMENT_NAME,
      RECOMMENDATION_TRACKING_KEYS.REC_PRODUCT_COUNT,
    ].reduce<Record<string, string | boolean>>((acc, key) => {
      const value = tabData?.[key]
      if (value !== undefined) acc[key] = value
      return acc
    }, {})

    const buyNowClickTrackConfig: TrackConfig = {
      trackGA: true,
      trackInternal: true,
      category: 'checkout',
      // @i18n-ignore
      action: 'click_quick_shop',
      context: {
        ...productClickTrackConfig?.context,
        product_id: prod.relatedIds.spid,
        internalProductId: `${prod.productId}_${selectedVariant?.displayConfig.value}`,
        product_search_query: searchQuery,
        product_position: position,
        product_price: selectedVariant?.price?.salePrice,
        name: prod.title,
        color_name: selectedVariant?.displayConfig.value,
        color_variant: prod.colorVariants,
        rating: prod.reviews.averageRating,
        productProperties,
        custom_variants: prod.customVariants,
        oos_flag: getIndividualTagStatus((selectedVariant?.tags ?? []).map(item => item.value as ProductTagKey))
          .sold_out,
      },
    }

    // Skip BUTTON_CLICK when quickBuyOverride is set — the caller owns tracking
    // in that path (e.g. cart-rec fires PRODUCT_CLICK via handleCardClick).
    // Firing it here would emit a net-new click_quick_shop/checkout event with
    // no legacy counterpart, inflating checkout-click metrics.
    if (!quickBuyOverride) {
      trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, buyNowClickTrackConfig)
    }

    handleTrackAddedToCartAlgolia({
      objectID: productSearchObjectId,
      queryID: siteStateManager.getSearchDetails().searchAlgoliaQueryId || '',
    })

    if (!selectedVariant?.price?.salePrice) return

    if (quickBuyOverride) {
      quickBuyOverride()
      return
    }

    productBuyNowStateManager.showBuyNowPopUp({
      productCard,
      selectedOptions: getSelectedOptionsForProductCard(selectedVariant),
      searchQuery,
      position,
      productClickTrackConfig,
      tabData,
    })
  }, [
    productCard,
    position,
    productClickTrackConfig,
    productSearchObjectId,
    quickBuyOverride,
    searchQuery,
    selectedVariant,
    tabData,
  ])

  const addToBagButtonCssResolved = css.raw({ flexShrink: 0, ...(addToBagButtonCss ?? {}) })

  if (!visible) return null

  return (
    <div
      data-slot="product-tile-add-bag"
      className={css({
        position: 'absolute',
        bottom: isMini ? 'space-8' : isCompact ? 'space-12' : { base: 'space-8', lg: 'space-16' },
        right: isMini ? 'space-8' : isCompact ? 'space-12' : { base: 'space-8', lg: 'space-16' },
        zIndex: 2,
        display: showBuyNowOnMobile ? 'flex' : { base: 'none', lg: 'flex' },
        alignItems: 'center',
        justifyContent: 'center',
        transitionProperty: 'opacity',
        transitionDuration: '0.15s',
        transitionTimingFunction: 'ease-out',
        '@media (prefers-reduced-motion: reduce)': {
          transitionDuration: '0.01ms',
        },
        ...(addToBagReveal === 'hover'
          ? {
              '@media (hover: none)': {
                opacity: 1,
                pointerEvents: 'auto',
              },
              '@media (hover: hover)': {
                opacity: 0,
                pointerEvents: 'none',
              },
            }
          : {
              opacity: 1,
              pointerEvents: 'auto',
            }),
      })}
    >
      <IconButton
        type="button"
        variant="secondaryOnColor"
        aria-label={resolvedAddToBagAriaLabel}
        onClick={handleClick}
        disabled={disabled}
        css={addToBagButtonCssResolved}
      >
        <BagPlusIcon width={20} height={20} title={resolvedAddToBagAriaLabel} />
      </IconButton>
    </div>
  )
}
